/**
 * useWebSocket — React hook for realtime hub subscriptions
 * Connects to ws://localhost:4000/ws, auto-reconnects, JWT auth, channel-based subscriptions.
 *
 * Channels:
 *  - audit:{tenantId}        — WORM audit events
 *  - observations:{tenantId} — IoMT device stream
 *  - consultations:{tenantId}— Encounter updates
 *  - alerts:{tenantId}       — Clinical alerts (drug interactions, abnormal vitals)
 */
import { useEffect, useRef, useState, useCallback } from "react";

const WS_URL = (import.meta.env.VITE_WS_URL as string) || "ws://localhost:4000/ws";

export type RealtimeChannel = `audit:${string}` | `observations:${string}` | `consultations:${string}` | `alerts:${string}`;

export interface RealtimeEvent<T = unknown> {
  channel: string;
  event: string;
  data: T;
  timestamp: string;
  actor?: string;
}

export interface UseWebSocketOptions {
  /** JWT token (HS256). If omitted, will read from localStorage "auth_token". */
  token?: string;
  /** Channels to subscribe to. */
  channels?: RealtimeChannel[];
  /** Tenant ID. If omitted, read from "tenant_id" in localStorage. */
  tenantId?: string;
  /** Auto-reconnect on close (default true). */
  autoReconnect?: boolean;
  /** Initial reconnect delay ms (default 1000, doubled each retry, capped at 30s). */
  reconnectDelay?: number;
  /** Auto-subscribe on connect. */
  autoSubscribe?: boolean;
}

export interface UseWebSocketReturn {
  connected: boolean;
  reconnecting: boolean;
  subscriptions: string[];
  lastEvent: RealtimeEvent | null;
  send: (data: unknown) => void;
  subscribe: (channel: string) => void;
  unsubscribe: (channel: string) => void;
  stats: { reconnectCount: number; receivedCount: number } | null;
}

export function useWebSocket(opts: UseWebSocketOptions = {}): UseWebSocketReturn {
  const {
    token,
    channels,
    tenantId,
    autoReconnect = true,
    reconnectDelay = 1000,
    autoSubscribe = true,
  } = opts;

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectCountRef = useRef(0);
  const receivedCountRef = useRef(0);

  const [connected, setConnected] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [subscriptions, setSubscriptions] = useState<string[]>([]);
  const [lastEvent, setLastEvent] = useState<RealtimeEvent | null>(null);

  const getToken = useCallback((): string | undefined => {
    if (token) return token;
    try {
      return localStorage.getItem("auth_token") || undefined;
    } catch {
      return undefined;
    }
  }, [token]);

  const getTenant = useCallback((): string => {
    if (tenantId) return tenantId;
    try {
      return localStorage.getItem("tenant_id") || "default";
    } catch {
      return "default";
    }
  }, [tenantId]);

  const connect = useCallback(() => {
    const authToken = getToken();
    if (!authToken) {
      console.warn("[useWebSocket] No JWT token, skipping connect");
      return;
    }
    const url = `${WS_URL}?token=${encodeURIComponent(authToken)}`;
    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        setReconnecting(false);
        reconnectCountRef.current = 0;
        if (autoSubscribe) {
          const tenant = getTenant();
          const targets: string[] = [];
          if (channels && channels.length > 0) {
            targets.push(...channels);
          } else {
            targets.push(`audit:${tenant}`, `observations:${tenant}`, `consultations:${tenant}`, `alerts:${tenant}`);
          }
          targets.forEach((c) => subscribe(c));
        }
      };

      ws.onmessage = (e) => {
        try {
          const evt: RealtimeEvent = JSON.parse(e.data);
          receivedCountRef.current += 1;
          setLastEvent(evt);
        } catch (err) {
          console.error("[useWebSocket] Bad message", err);
        }
      };

      ws.onerror = () => {
        // close handler will follow
      };

      ws.onclose = () => {
        setConnected(false);
        if (autoReconnect) {
          setReconnecting(true);
          const delay = Math.min(reconnectDelay * Math.pow(2, reconnectCountRef.current), 30000);
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectCountRef.current += 1;
            connect();
          }, delay);
        }
      };
    } catch (err) {
      console.error("[useWebSocket] Connect failed", err);
    }
  }, [autoReconnect, autoSubscribe, channels, getTenant, getToken, reconnectDelay]);

  const subscribe = useCallback(
    (channel: string) => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
      wsRef.current.send(JSON.stringify({ type: "subscribe", channel }));
      setSubscriptions((prev) => (prev.includes(channel) ? prev : [...prev, channel]));
    },
    []
  );

  const unsubscribe = useCallback((channel: string) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    wsRef.current.send(JSON.stringify({ type: "unsubscribe", channel }));
    setSubscriptions((prev) => prev.filter((c) => c !== channel));
  }, []);

  const send = useCallback((data: unknown) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    wsRef.current.send(typeof data === "string" ? data : JSON.stringify(data));
  }, []);

  useEffect(() => {
    connect();
    return () => {
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.close();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    connected,
    reconnecting,
    subscriptions,
    lastEvent,
    send,
    subscribe,
    unsubscribe,
    stats: { reconnectCount: reconnectCountRef.current, receivedCount: receivedCountRef.current },
  };
}

export default useWebSocket;